import React, { useState, Fragment, FC, FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Confirmation } from '../shared/Confirmation'
import { Dialog } from 'components/Dialog'
import { Icon } from 'components/Icon'
import { Heading } from 'components/Heading'
import { Stack } from 'components/Stack'
import { TextInput } from 'components/TextInput'
import { Text } from 'components/Text'
import { Alert } from 'components/Alert'
import { Button } from 'components/Button'
import { Badge } from 'components/Badge'
import { Flex } from 'components/Flex'
import { Spinner } from 'components/Spinner'
import { useMapEditorContext } from 'context/map-editor-context'
import { useFetchOrganization } from 'services/organization'
import { PointCoordinates } from 'models/map'
import { coordinatesToLngLat } from 'utils/coordinates'
import { useUserContext } from 'context/user-context'
import { useParams } from 'react-router-dom'
import { ApiProvider } from 'providers/request'
import { StorageProvider } from 'providers/storage'
import styles from './KeywordsReportWizard.module.scss'

export const KeywordsReportWizard: FC = () => {
  const { t } = useTranslation()
  const { projectId } = useParams()
  const [currentUser] = useUserContext()
  const [{ confirmed, editor }, mapEditorDispatch] = useMapEditorContext()
  const { organization, status: orgStatus } = useFetchOrganization()
  const coordinates = editor.feature?.geometry?.coordinates as PointCoordinates
  const [step, setStep] = useState<string>('setAttractors')
  const [attractorName, setAttractorName] = useState<string | undefined>(undefined)
  const [attractorsArray, setAttractorsArray] = useState<string[]>([])
  const [reportName, setReportName] = useState<string | undefined>(undefined)
  const [noReportName, setNoReportName] = useState<boolean>(false)
  const [tabMenu, setTabMenu] = useState<boolean>(true)

  React.useEffect(() => {
    try {
      const requestKeywords = async () => {
        const res = await ApiProvider.get('/reportkeywords')
        const data = JSON.parse(JSON.stringify(res))
        const keywords = Object.values(data.reportkeywords)

        keywords.map((keyword) => {
          if (!keyword) {
            return setAttractorsArray((attractorsArray) => [...attractorsArray, 'NO VÁLIDO'])
          } else if (typeof keyword === 'string') {
            return setAttractorsArray((attractorsArray) => [...attractorsArray, keyword])
          } else {
            return setAttractorsArray((attractorsArray) => [...attractorsArray, 'NO VÁLIDO'])
          }
        })

        keywords.length > 2 && setStep('setReportName')
      }

      requestKeywords()
    } catch (err) {
      console.error(err)
    }
  }, [])

  const onDialogClose = () => {
    mapEditorDispatch.cancelWizard()
  }

  const onAttractorSubmit = (e: FormEvent) => {
    e.preventDefault()

    if (typeof attractorName === 'string') {
      try {
        ApiProvider.post(`/reportkeywords?k=${attractorName}`)

        setAttractorsArray((attractorsArray) => [...attractorsArray, attractorName])

        setAttractorName('')
      } catch (err) {
        console.error(err)
      }
    }
  }

  const onSubmitReport = () => {
    const orgid = organization?.id

    if (!reportName) {
      setNoReportName(true)

      setTimeout(() => {
        setNoReportName(false)
      }, 5000)
    } else {
      const reportRequestData = {
        ...coordinatesToLngLat(coordinates),
        projectId,
        orgid,
      }

      getReportData(reportRequestData)
    }
  }

  const getReportData = async (reportRequestData: any) => {
    setStep('loading')
    setTabMenu(false)
    const name = reportName
    const availableResidences = 0
    const locationUrl = window.location.href
    const urlArr = locationUrl.split('/')
    const projectArrId = urlArr[4]
    const userid = currentUser?.id
    const language = StorageProvider.get('language')

    const customReportUrl =
      'https://report.descifra.global/GetReports.ashx?idorg=' +
      reportRequestData.orgid +
      '&lat=' +
      reportRequestData.lat +
      '&lng=' +
      reportRequestData.lng +
      '&nombre=' +
      name +
      '&Key=RGVzY2lmcmEwOTIz&correo=' +
      String(currentUser!.email) +
      '&type=custom&mr=' +
      String(organization!.mobilityReport) +
      '&pr=' +
      String(organization!.predictiveReport) +
      '&projectid=' +
      String(projectArrId) +
      '&userid=' +
      String(userid) +
      '&totalhog=' +
      String(availableResidences) +
      '&language=' +
      language +
      '&format=general'

    const data = await fetch(customReportUrl)
    const res = await data.json()

    if (res[0].status === '200') {
      setStep('ready')
    } else {
      alert('Error en la generación del reporte, contacta a soporte.')
    }
  }

  const onDeleteAttractor = (attractor: string) => {
    try {
      ApiProvider.delete(`/reportkeywords/${attractor}`)

      setAttractorsArray(attractorsArray.filter((item) => item !== attractor))
    } catch (err) {
      console.error(err)
    }
  }

  if (!organization || orgStatus === 'loading') return null
  if (confirmed) {
    return (
      <Dialog
        isOpen={confirmed}
        onClose={onDialogClose}
        aria-label={t(`keywords_report_dialog.${step}.title`)}
        className={styles.saladReportWizard}
      >
        <Stack space="medium">
          <div className={styles.headingBar}>
            <Icon className={styles.headingIcon} name="file-chart-line" size="xl" />
            <Heading className={styles.heading}>{t(`keywords_report_dialog.title`)}</Heading>
          </div>
        </Stack>

        {tabMenu && (
          <Stack space="medium">
            <br />
            <nav className={styles.tabMenu}>
              <button
                className={step === 'setAttractors' ? styles.tabButtonActive : styles.tabButton}
                onClick={(): void => setStep('setAttractors')}
              >
                <Text align="center">
                  <p className={step === 'setAttractors' ? styles.textColor : undefined}>
                    {t(`keywords_report_dialog.tabs.attractors`)}
                  </p>
                </Text>
              </button>
              <button
                className={step === 'setReportName' ? styles.tabButtonActive : styles.tabButton}
                onClick={(): void => setStep('setReportName')}
              >
                <Text align="center">
                  <p className={step === 'setReportName' ? styles.textColor : undefined}>
                    {t(`keywords_report_dialog.tabs.report`)}
                  </p>
                </Text>
              </button>
            </nav>
          </Stack>
        )}

        <br />

        {organization.keywordsReport !== null && (
          <Fragment>
            {organization.keywordsReport! < 1 && (
              <Stack space="medium">
                <Alert full tone="critical">
                  No tienes reportes restantes
                </Alert>
                <Text>Contacta al área de success para adquirir más reportes</Text>
                <Button onClick={onDialogClose} full>
                  Regresar
                </Button>
              </Stack>
            )}

            {organization.keywordsReport! >= 1 && (
              <Stack space="medium">
                {step === 'setAttractors' && (
                  <Fragment>
                    <form className={styles.reportForm} onSubmit={onAttractorSubmit}>
                      <TextInput
                        disabled={attractorsArray.length >= 7 && true}
                        name="attractors"
                        label={t(`keywords_report_dialog.setAttractors.placeholder`)}
                        value={attractorName}
                        onChange={(event) => setAttractorName(event.target.value)}
                      />
                      <Text size="small">{t(`keywords_report_dialog.setAttractors.cta`)}</Text>
                      <div className={styles.addButtonWrapper}>
                        <Button
                          disabled={!attractorName && true}
                          type="submit"
                          size="small"
                          variant="outline"
                        >
                          {t(`keywords_report_dialog.setAttractors.buttons.add_attractor`)}
                        </Button>
                      </div>
                    </form>
                    <br />
                    <Text>{t(`keywords_report_dialog.setAttractors.added_attractors`)}</Text>
                    <br />

                    {attractorsArray.length === 0 && (
                      <Fragment>
                        <Alert tone="critical" full>
                          {t(`keywords_report_dialog.setAttractors.no_attractors`)}
                        </Alert>
                        <br />
                        <br />
                      </Fragment>
                    )}

                    {attractorsArray.length > 0 && (
                      <Stack space="medium">
                        <div className={styles.badgeWrapper}>
                          {attractorsArray.map((element: any, index: any) => (
                            <div className={styles.badgeContainer}>
                              <Badge key={index}>
                                {element}{' '}
                                <button
                                  onClick={(): void => onDeleteAttractor(element)}
                                  className={styles.deleteAttractor}
                                >
                                  x
                                </button>
                              </Badge>
                            </div>
                          ))}
                          <br />
                        </div>
                      </Stack>
                    )}

                    <Button
                      onClick={(): void => setStep('setReportName')}
                      disabled={attractorsArray.length < 2 && true}
                      full
                    >
                      {t(`keywords_report_dialog.setAttractors.buttons.continue`)}
                    </Button>
                    <br />
                    <Text>
                      {organization.keywordsReport}{' '}
                      {t(`keywords_report_dialog.setAttractors.remaining_reports`)}
                    </Text>
                  </Fragment>
                )}

                {step === 'setReportName' && (
                  <Fragment>
                    <Text weight="strong">{t(`keywords_report_dialog.setReportName.cta`)}</Text>
                    <br />
                    <TextInput
                      name="reportName"
                      label="Escriba el nombre del reporte"
                      value={reportName}
                      onChange={(event) => setReportName(event.target.value)}
                    />
                    {noReportName && (
                      <Fragment>
                        <br />
                        <Alert tone="critical" full>
                          {t(`keywords_report_dialog.setReportName.no_report_name`)}
                        </Alert>
                        <br />
                      </Fragment>
                    )}
                    <br />
                    <Button full onClick={onSubmitReport}>
                      {t(`keywords_report_dialog.setReportName.cta`)}
                    </Button>
                  </Fragment>
                )}

                {step === 'loading' && (
                  <Fragment>
                    <div className={styles.iconWrapper}>
                      <Icon className={styles.iconImage} name="mail-send-line" size="xl" />
                      <Text className={styles.iconSideText}>
                        {t(`keywords_report_dialog.${step}.cta`)}
                      </Text>
                    </div>
                    <br />
                    <Flex justify="center" align="center">
                      <Spinner />
                    </Flex>
                  </Fragment>
                )}

                {step === 'ready' && (
                  <div className={styles.iconWrapper}>
                    <Icon className={styles.iconImage} name="mail-send-line" size="xl" />
                    <Text className={styles.iconSideText}>
                      {t(`keywords_report_dialog.${step}.cta`)}
                    </Text>
                  </div>
                )}
              </Stack>
            )}
          </Fragment>
        )}
      </Dialog>
    )
  }
  return <Confirmation onConfirm={() => mapEditorDispatch.setConfirm(true)} />
}

KeywordsReportWizard.displayName = 'KeywordsReportWizard'
